import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { motion } from "framer-motion";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DomainIcon from "@mui/icons-material/Domain";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useTranslation } from "react-i18next";

const milestones = [
  { year: "1974", labelKey: "milestones.alMansouriyah", icon: <BusinessIcon fontSize="inherit" /> },
  { year: "1977", labelKey: "milestones.industrial", icon: <EngineeringIcon fontSize="inherit" /> },
  { year: "1984", labelKey: "milestones.turath", icon: <DomainIcon fontSize="inherit" /> },
  { year: "1995", labelKey: "milestones.realEstateManagement", icon: <LocationCityIcon fontSize="inherit" /> },
  { year: "1996", labelKey: "milestones.medicalOffice", icon: <LocalHospitalIcon fontSize="inherit" /> },
  { year: "2006", labelKey: "milestones.medicalEngineering", icon: <ApartmentIcon fontSize="inherit" /> },
  { year: "2011", labelKey: "milestones.raeal", icon: <DomainIcon fontSize="inherit" /> },
  { year: "2025", labelKey: "milestones.luluatAlOmran", icon: <BusinessIcon fontSize="inherit" /> },
];

const text3DStyle = {
  color: "dodgerblue",
  fontWeight: "bold",
  textShadow: `
    1px 1px 0 #a0cfff,
    2px 2px 0 #7db7ff,
    3px 3px 0 #599fff,
    4px 4px 6px rgba(0, 102, 255, 0.6)
  `,
  transformStyle: "preserve-3d",
  textAlign: "center",
};

const AboutHistory = () => {
  const { t, i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";
 const isRTL = i18n.language === 'ar';

  const fontFamily = isRTL ? "'Tajawal', sans-serif" : 'inherit';
  return (
    <Box
      dir={direction}
      sx={{ p: { xs: 2, md: 6 }, backgroundColor: "lightgray", m: 4, borderRadius: "30px" }}
    >
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <Typography
        fontFamily={fontFamily}
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            color: "#007BFF",
            mb: 4,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            opacity: 0,
            transform: "translateY(30px)",
            animation: "fadeSlideUp 1.2s ease-out forwards, pulseGlow 3s ease-in-out infinite",
            textShadow: "2px 2px 6px rgba(0, 123, 255, 0.4)",
            transition: "color 0.3s ease, text-shadow 0.3s ease",
            "&:hover": {
              color: "#0056b3",
              textShadow: "3px 3px 12px rgba(0, 86, 179, 0.9)",
            },
          }}
        >
          {t("history.title")}
        </Typography>

        <Typography   fontFamily={fontFamily} variant="h6" sx={text3DStyle} gutterBottom>
          {t("history.subtitle")}
        </Typography>

        <Typography   fontFamily={fontFamily} variant="body1" paragraph sx={{ textAlign: "center", maxWidth: 700, mx: "auto" }}>
          {t("history.paragraph1")}
        </Typography>

        <Typography   fontFamily={fontFamily} variant="body1" paragraph sx={{ textAlign: "center", maxWidth: 700, mx: "auto" }}>
          {t("history.paragraph2")}
        </Typography>
      </motion.div>

      {/* Timeline Section */}
      <Box sx={{ mt: 8 }}>
        <Typography   fontFamily={fontFamily} variant="h5" sx={text3DStyle} gutterBottom>
          {t("milestones.title")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 4,
            py: 4,
            px: 1,
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#bbb",
              borderRadius: 4,
            },
          }}
        >
          {milestones.map((milestone, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ rotateY: 10, rotateX: 10, scale: 1.03 }}
                style={{ perspective: 1000 }}
              >
                <Paper
                  elevation={10}
                  sx={{
                    minWidth: 260,
                    p: 3,
                    borderRadius: 3,
                    textAlign: "center",
                    background: "#ffffff",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.3s ease",
                    borderTop: `4px solid dodgerblue`,
                    boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
                  }}
                >
                  <Box sx={{ mb: 2, color: "dodgerblue", fontSize: 50 }}>{milestone.icon}</Box>
                  <Typography   fontFamily={fontFamily} variant="h6" fontWeight="bold">
                    {milestone.year}
                  </Typography>
                  <Typography   fontFamily={fontFamily} variant="body2">{t(milestone.labelKey)}</Typography>
                </Paper>
              </motion.div>

              {/* Arrow line except after the last item */}
              {index < milestones.length - 1 && (
                <Box
                  sx={{
                    width: 40,
                    height: 2,
                    background: "dodgerblue",
                    mx: 1,
                    position: "relative",
                    ...(direction === "rtl"
                      ? {
                          // Arrow pointing left
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "-4px",
                            left: 0,
                            width: 0,
                            height: 0,
                            borderTop: "6px solid transparent",
                            borderBottom: "6px solid transparent",
                            borderRight: "10px solid dodgerblue",
                          },
                        }
                      : {
                          // Arrow pointing right (default)
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "-4px",
                            right: 0,
                            width: 0,
                            height: 0,
                            borderTop: "6px solid transparent",
                            borderBottom: "6px solid transparent",
                            borderLeft: "10px solid dodgerblue",
                          },
                        }),
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Today Section */}
      <Box sx={{ mt: 8 }}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <Typography   fontFamily={fontFamily} variant="h6" sx={text3DStyle} gutterBottom>
            {t("today.title")}
          </Typography>
          <Typography   fontFamily={fontFamily} variant="body1" sx={{ textAlign: "center", maxWidth: 700, mx: "auto" }}>
            {t("today.description")}
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AboutHistory;
